/**
 * The brand people see on every surface: the apps, emails, bot prompts, the marketing site and
 * the docs. Change the name or domain here and everything follows; `docs/nexamero/brand.md` lists
 * the few places outside TypeScript that cannot import this file.
 *
 * Copy interpolates the name (translations carry a `{PRODUCT_NAME}` placeholder), so it is never
 * translated. Package names, identifiers, env vars and protocol strings keep `rakazo`.
 */

/** The public domain. The site, the app, the docs and the mailboxes all derive from it. */
const DOMAIN = "nexamero.com";

export const BRAND = {
  productName: "Nexamero",
  /** The legal entity that runs the service, as on the legal pages and in structured data. */
  companyName: "[Your company name]",
  domain: DOMAIN,
  siteUrl: `https://${DOMAIN}`,
  appUrl: `https://app.${DOMAIN}`,
  docsUrl: `https://${DOMAIN}/docs`,
  supportEmail: `support@${DOMAIN}`,
  privacyEmail: `privacy@${DOMAIN}`,
  securityEmail: `security@${DOMAIN}`,
  /** Public profiles. A `null` profile is never linked. */
  social: {
    x: null as string | null,
    linkedin: null as string | null,
    github: null as string | null,
  },
  /**
   * `owner/name` of the public GitHub repository desktop releases publish to and the desktop
   * update feed reads. The release workflow's `RELEASE_REPO` variable overrides it per fork.
   */
  releaseRepo: "bionanite/nexamero-releases",
  /**
   * The mobile app's identity in the stores, read by `apps/mobile/app.config.ts`. The bundle ID
   * (also the Android package) cannot change once a build is uploaded to a store. The scheme
   * opens the app's links, receives sign-in and billing returns, and is the origin the app
   * sends to the server.
   */
  mobile: {
    bundleId: "com.nexamero.app",
    scheme: "nexamero",
    /** The Expo project slug, matching the project `eas init` links. */
    slug: "nexamero",
  },
} as const;

export const PRODUCT_NAME = BRAND.productName;

/** The origin the mobile app sends with its requests and the root of its links: `<scheme>://`. */
export const MOBILE_APP_ORIGIN = `${BRAND.mobile.scheme}://`;

/** A page of the user docs on the marketing site, as Markdown (`docs/<slug>.md`). */
export function docsMarkdownUrl(slug?: string): string {
  return slug ? `${BRAND.docsUrl}/${slug}.md` : `${BRAND.docsUrl}.md`;
}
